import React, { useState, ChangeEvent, useEffect } from "react";
import "./CategoryModal.css";
import Select from "../../../../components/reusable-select/Select";
import InputBox from "../../../../components/reusable-input/InputBox";
import Switch from "../../../../components/reusable-switch/Switch";
import Button from "../../../../components/reusable-button/Button";
import { MdDeleteForever } from "react-icons/md";
import { useDispatch } from "react-redux";
import { showSnackbar } from "../../../../redux/slices/snackbarSlice";

const DeleteIcon = MdDeleteForever as unknown as React.FC<
  React.SVGProps<SVGSVGElement>
>;
interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: CategoryFormData) => void;
  mode: "add" | "edit";
  initialData?: CategoryFormData;
  loading?: boolean;
  categoryTypeOptions: { label: string; value: string }[];
}

export interface CategoryFormData {
  logo?: File | null;
  logoUrl?: string;
  type: string;
  name: string;
  description: string;
  active: boolean;
  authType?: string;
  fileId?: string;
}

const CategoryModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onSave,
  mode,
  initialData,
  loading = false,
  categoryTypeOptions,
}) => {
  const dispatch = useDispatch();
  const [logo, setLogo] = useState<File | null>(null);
  const [logoUrl, setLogoUrl] = useState<string | undefined>("");
  const [type, setType] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [active, setActive] = useState(true);
  const [fileId, setFileId] = useState<string | undefined>("");
  const [error, setError] = useState(false);

  useEffect(() => {
    if (mode === "edit" && initialData) {
      setLogo(null);
      setLogoUrl(initialData.logoUrl);
      setType(initialData.type);
      setName(initialData.name);
      setDescription(initialData.description);
      setActive(initialData.active);
      setFileId(initialData.fileId);
    } else {
      setLogo(null);
      setLogoUrl("");
      setType("");
      setName("");
      setDescription("");
      setActive(true);
    }
  }, [mode, initialData, isOpen]);

  const handleLogoChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];

      // 2 MB limit in bytes
      const maxSize = 2 * 1024 * 1024;
      if (file.size > maxSize) {
        dispatch(
          showSnackbar({
            message: "File size exceeds 2 MB",
            type: "error",
          })
        );
        e.target.value = ""; // reset input
        return;
      }

      setLogo(file);
      setLogoUrl(URL.createObjectURL(file));
      setError(false);
    }
  };

  const handleRemoveLogo = () => {
    setLogo(null);
    setLogoUrl("");
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!logo && !logoUrl) {
      setError(true);
      return;
    }
    onSave({
      logo,
      logoUrl,
      type,
      name,
      description,
      active,
      fileId,
    });
    // onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="category-modal-overlay">
      <div className="category-modal">
        <h3 className="category-modal-title">
          {mode === "add" ? "Add Category" : "Edit Category"}
        </h3>
        <form onSubmit={handleSubmit}>
          {/* Logo Upload */}
          <label className="modal-label">
            Category logo <span className="required">*</span>
          </label>
          <div
            className="upload-box"
            style={{ borderColor: error ? "red" : "var(--primary-color)" }}
          >
            {logoUrl ? (
              <>
                <div className="uploaded-image">
                  <img src={logoUrl} alt="Category" />
                </div>
                <button
                  type="button"
                  className="remove-button"
                  onClick={handleRemoveLogo}
                >
                  <DeleteIcon />
                </button>
              </>
            ) : (
              <>
                <input
                  type="file"
                  accept="image/*"
                  id="fileInput"
                  onChange={handleLogoChange}
                  hidden
                />
                <label htmlFor="fileInput" className="upload-placeholder">
                  Upload an Image
                  <span className="upload-subtext">(2 MB Max)</span>
                </label>
              </>
            )}
          </div>
          {error && (
            <p className="error-text">Please upload a valid category logo.</p>
          )}

          <Select
            label="Select Category Type"
            name="category-type"
            options={categoryTypeOptions}
            value={type}
            onChange={(e) => setType(e.target.value)}
            required
          />

          <InputBox
            label="Category Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            placeholder="Enter category name"
            maxLength={120}
          />

          {/* Description */}
          <div className="modal-textarea">
            <label className="modal-label">
              Description <span className="required">*</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
              placeholder="Enter description"
            />
          </div>

          {initialData && (
            <Switch
              checked={active}
              onChange={() => setActive(!active)}
              label={active ? "Active" : "Inactive"}
            />
          )}

          {/* Buttons */}
          <div className="category-modal-actions">
            <Button
              variant="outlined"
              label="Cancel"
              type="button"
              onClick={onClose}
            />
            <Button
              label={mode === "add" ? "Add Category" : "Save Changes"}
              type="submit"
              loading={loading}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default CategoryModal;
